import { createFileRoute, notFound } from '@tanstack/react-router';
import { z } from 'zod';
import { RequireService } from '@/components/shared/require-service';
import { TransactionListPage } from '@/features/transactions/transaction-list-page';

/**
 * One param route for all twelve transaction lists (fuel.md sections 3 and
 * 5.2): `/diesel/purchases`, `/petrol/debt-movements`, etc. Both `$product`
 * and `$family` are validated here; an unknown value is a 404, not a
 * silently-broken page.
 */
export const PRODUCT_SLUGS = ['diesel', 'petrol'] as const;
export type ProductSlug = (typeof PRODUCT_SLUGS)[number];

export const PRODUCT_ACCESS_SERVICE: Record<ProductSlug, string> = {
  diesel: 'DIESEL_ACCESS',
  petrol: 'PETROL_ACCESS',
};

export const FAMILY_SLUGS = [
  'revolving-balances',
  'purchases',
  'sales',
  'debt-movements',
  'payments',
  'receipts',
] as const;
export type FamilySlug = (typeof FAMILY_SLUGS)[number];

export type TxEntity =
  'REVOLVING_BALANCE' | 'PURCHASE' | 'SALE' | 'DEBT_MOVEMENT' | 'PAYMENT' | 'RECEIPT';

export const FAMILY_ENTITY: Record<FamilySlug, TxEntity> = {
  'revolving-balances': 'REVOLVING_BALANCE',
  purchases: 'PURCHASE',
  sales: 'SALE',
  'debt-movements': 'DEBT_MOVEMENT',
  payments: 'PAYMENT',
  receipts: 'RECEIPT',
};

interface TransactionListParams {
  product: ProductSlug;
  family: FamilySlug;
}

function isProductSlug(value: string): value is ProductSlug {
  return (PRODUCT_SLUGS as readonly string[]).includes(value);
}
function isFamilySlug(value: string): value is FamilySlug {
  return (FAMILY_SLUGS as readonly string[]).includes(value);
}

/** fuel.md section 5.2: filters live in the URL search params. */
export const transactionListSearchSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().positive().optional(),
  search: z.string().optional(),
  movementId: z.string().min(1).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  branchId: z.string().optional(),
  deliveryStatus: z.enum(['PENDING', 'DELIVERED']).optional(),
  employeeId: z.string().optional(),
  debtorId: z.string().optional(),
  vehicleId: z.string().optional(),
  kind: z.enum(['CHARGE', 'REPAYMENT']).optional(),
  /** Debt movements only (KAN-28): حالة الترحيل. */
  postingStatus: z.enum(['POSTED', 'PENDING']).optional(),
  /** Purchases only (fuel-pda.md section 6.1): حالة التسعير. */
  priceStatus: z.enum(['PRICED', 'PENDING']).optional(),
});
export type TransactionListSearch = z.infer<typeof transactionListSearchSchema>;

function TransactionListRoute() {
  const { product, family } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const entity = FAMILY_ENTITY[family];

  return (
    <RequireService service={[`${entity}_LIST`, PRODUCT_ACCESS_SERVICE[product]]}>
      <TransactionListPage product={product} family={family} search={search} navigate={navigate} />
    </RequireService>
  );
}

export const Route = createFileRoute('/_app/$product/$family')({
  params: {
    parse: (raw): TransactionListParams => {
      if (!isProductSlug(raw.product) || !isFamilySlug(raw.family)) {
        throw notFound();
      }
      return { product: raw.product, family: raw.family };
    },
    stringify: (params) => params,
  },
  validateSearch: transactionListSearchSchema,
  component: TransactionListRoute,
});
